"use client"

import { useState, useEffect } from "react"
import {
  MapPin,
  Mail,
  Phone,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Anchor,
  Loader2,
  Send,
  ExternalLink,
} from "lucide-react"

// For local development
const API_URL = "http://localhost:5002"

// Log the API URL for debugging
console.log("API URL:", API_URL)

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [currentAdIndex, setCurrentAdIndex] = useState(0)

  // Real sponsors data
  const sponsors = {
    home: [
      {
        name: "Ethiopian Airlines",
        logo: "/assets/images/airlines.png",
        tagline: "Africa's Largest Aviation Group",
        description: "Connecting Africa to the world with excellence in aviation services.",
        website: "https://www.ethiopianairlines.com/",
        category: "Aviation",
      },
      {
        name: "Ethiopian Maritime Authority",
        logo: "/assets/images/ema.png",
        tagline: "Leading Maritime Excellence",
        description: "Regulating and promoting Ethiopia's maritime industry development.",
        website: "https://etmaritime.com/",
        category: "Maritime",
      },
      {
        name: "EMA Marine ",
        logo: "/assets/images/EMA_logo.png",
        tagline: "Global Maritime Solutions",
        description: "Providing comprehensive maritime services and crew management.",
        website: "https://www.emacrew.com/",
        category: "Marine Services",
      },
    ],
    about: [
      {
        name: "Ministry of Transport & Logistics",
        logo: "/assets/images/MoTL.jpg",
        tagline: "Building Ethiopia's Future",
        description: "Developing transportation infrastructure and logistics policies.",
        website: "https://www.motl.gov.et/",
        category: "Government",
      },
      {
        name: "Ethiopian Maritime Training Institute",
        logo: "/assets/images/emtisc.jpg",
        tagline: "Training Tomorrow's Leaders",
        description: "World-class maritime education and professional training programs.",
        website: "https://emticorp.com/",
        category: "Education",
      },
    ],
  }

  // Combine all sponsors for rotation
  const allSponsors = [...sponsors.home, ...sponsors.about]

  // Auto-rotate ads
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentAdIndex((prev) => (prev + 1) % allSponsors.length)
    }, 6000)
    return () => clearInterval(interval)
  }, [allSponsors.length])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)
    setSubmitError("")

    try {
      const endpoint = `${API_URL}/api/contact`
      console.log("Submitting form to:", endpoint)
      console.log("Form data:", formData)

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
        credentials: "include", // Include cookies for CORS
      })

      console.log("Response status:", response.status)

      // Check if response is JSON before trying to parse it
      const contentType = response.headers.get("content-type")
      let data
      if (contentType && contentType.includes("application/json")) {
        data = await response.json()
      } else {
        const text = await response.text()
        console.error("Non-JSON response:", text)
        throw new Error("Server returned non-JSON response")
      }

      if (!response.ok) {
        throw new Error(data.message || `Server returned ${response.status}`)
      }

      // Reset form
      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      })
      setSubmitSuccess(true)
    } catch (error) {
      console.error("Error submitting form:", error)
      setSubmitError(`Error: ${error.message}. Please try again or contact us directly at mail@africamaritime.com`)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Subtle ad components
  const TopBannerAd = ({ sponsor }) => (
    <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
      <div className="max-w-6xl mx-auto px-4 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Sponsored</span>
            <img
              src={sponsor.logo || "/placeholder.svg"}
              alt={sponsor.name}
              className="h-6 w-auto max-w-[80px] object-contain"
            />
            <div className="hidden sm:block">
              <span className="text-sm font-medium text-gray-700">{sponsor.name}</span>
              <span className="text-xs text-gray-500 ml-2">• {sponsor.tagline}</span>
            </div>
          </div>
          <a
            href={sponsor.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs text-gray-500 hover:text-gray-700 transition-colors flex items-center gap-1"
          >
            Learn More <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>
    </div>
  )

  const FormSideAd = ({ sponsor }) => (
    <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Sponsored</span>
        <span className="text-xs text-gray-400">{sponsor.category}</span>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <img
          src={sponsor.logo || "/placeholder.svg"}
          alt={sponsor.name}
          className="h-8 w-auto max-w-[60px] object-contain"
        />
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-800 truncate">{sponsor.name}</h4>
          <p className="text-xs text-gray-500 truncate">{sponsor.tagline}</p>
        </div>
      </div>
      <p className="text-xs text-gray-600 mb-3 line-clamp-2">{sponsor.description}</p>
      <a
        href={sponsor.website}
        target="_blank"
        rel="noopener noreferrer"
        className="text-xs text-[#A65C4A] hover:text-gray-800 transition-colors flex items-center gap-1"
      >
        Visit Website <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  )

  const InlineAd = ({ sponsor }) => (
    <div className="bg-gradient-to-r from-gray-50 to-white border border-gray-200 rounded-lg p-4 my-8">
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-gray-500 bg-gray-200 px-2 py-1 rounded">Partnership Spotlight</span>
        <span className="text-xs text-gray-400">{sponsor.category}</span>
      </div>
      <div className="flex items-center gap-4">
        <img
          src={sponsor.logo || "/placeholder.svg"}
          alt={sponsor.name}
          className="h-10 w-auto max-w-[80px] object-contain"
        />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-gray-800 mb-1">{sponsor.name}</h3>
          <p className="text-xs text-gray-600 mb-2">{sponsor.tagline}</p>
          <p className="text-xs text-gray-500 line-clamp-1">{sponsor.description}</p>
        </div>
        <a
          href={sponsor.website}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-[#A65C4A] hover:bg-[#A65C4A]/90 text-white text-xs px-3 py-1.5 rounded transition-colors flex items-center gap-1"
        >
          Learn More <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  )

  const currentSponsor = allSponsors[currentAdIndex]

  return (
    <section className="min-h-screen bg-white" style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}>

      {/* Hero Section */}
      <section
        className="relative  bg-[#0E6FA9] overflow-hidden"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
      >
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/images/contactimage.jpg"
            alt="Maritime background"
            className="absolute inset-0 w-full h-full object-cover opacity-7"
          />
        </div>
        <div className="absolute inset-0 bg-[#0E6FA9]/80"></div>
        <div className="relative z-10 text-center text-white px-4 max-w-4xl mx-auto">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-[#A65C4A]/10 rounded-full border-2 border-[#A65C4A]">
              <Anchor className="h-12 w-12 text-[#A65C4A]" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">Contact Us</h1>
          <div className="h-1 w-24 bg-[#A65C4A] mx-auto mb-6"></div>
          <p className="text-xl md:text-2xl text-white/90 font-light">
            Get in touch with the Africa Maritime Conference Team
          </p>
        </div>
      </section>

      <div className="py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Main Contact Form */}
            <div className="bg-white rounded-lg shadow-lg border border-primary-navy/10 overflow-hidden">
              {submitSuccess ? (
                <div className="p-8 text-center">
                  <div className="mx-auto w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="text-green-500"
                    >
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                  </div>
                  <h3 className="text-2xl font-bold text-primary-navy mb-3">Message Received!</h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    Thank you for contacting us. We've received your message and our team will get back to you within
                    24-48 hours.
                  </p>
                  <button
                    onClick={() => setSubmitSuccess(false)}
                    className="bg-primary-orange hover:bg-primary-orange/90 text-white px-6 py-2.5 rounded-md transition-colors font-medium flex items-center gap-2 mx-auto"
                  >
                    <Send size={16} />
                    Send Another Message
                  </button>
                </div>
              ) : (
                <div className="p-8">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {submitError && <div className="p-4 bg-red-50 text-red-600 rounded-md mb-6">{submitError}</div>}
                    <div className="space-y-2">
                      <label htmlFor="name" className="block text-primary-navy font-medium">
                        Your Name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your full name"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="email" className="block text-primary-navy font-medium">
                        Email Address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter your email address"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="subject" className="block text-primary-navy font-medium">
                        Subject
                      </label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="What is this regarding?"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      />
                    </div>
                    <div className="space-y-2">
                      <label htmlFor="message" className="block text-primary-navy font-medium">
                        Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Type your message here..."
                        rows="5"
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-blue focus:border-transparent"
                      ></textarea>
                    </div>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary-orange hover:bg-primary-orange/90 text-white py-3 px-6 rounded-md transition-colors font-medium flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="animate-spin h-5 w-5" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send size={18} />
                          Send Message
                        </>
                      )}
                    </button>
                  </form>

                  {/* Sponsor Ads next to form */}
                  <div className="mt-8 space-y-4">
                    <div className="text-xs text-gray-500 text-center mb-4">Our Partners</div>
                    <div className="grid grid-cols-1 gap-4">
                      <FormSideAd sponsor={sponsors.home[1]} />
                      <FormSideAd sponsor={sponsors.about[1]} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Contact Info Sidebar */}
            <div className="grid grid-cols-1 gap-6 mt-0 md:mt-0">
              <div className="bg-white rounded-lg shadow-md overflow-hidden border-b-4 border-[#A65C4A]">
                <div className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 rounded-full bg-gray-100 text-[#A65C4A]">
                      <MapPin className="h-8 w-8" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Our Location</h3>
                  <p className="text-gray-600">Addis Ababa, Ethiopia</p>
                  <p className="text-gray-600">Bole Sub-City, Woreda 03</p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden border-b-4 border-[#A65C4A]">
                <div className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 rounded-full bg-gray-100 text-[#A65C4A]">
                      <Mail className="h-8 w-8" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Email Us</h3>
                  <p className="mb-2">
                    <a
                      href="mailto:info@africamaritimetalent.com"
                      className="text-[#A65C4A] hover:text-gray-800 transition-colors"
                    >
                      info@africamaritimeconference.com
                    </a>
                  </p>
                  <p>
                    <a
                      href="mailto:support@africamaritimetalent.com"
                      className="text-[#A65C4A] hover:text-gray-800 transition-colors"
                    >
                      support@africamaritimeconference.com
                    </a>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden border-b-4 border-[#A65C4A]">
                <div className="p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <div className="p-4 rounded-full bg-gray-100 text-[#A65C4A]">
                      <Phone className="h-8 w-8" />
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-3">Call Us</h3>
                  <p className="mb-2">
                    <a href="tel:+251 970 317 022" className="text-[#A65C4A] hover:text-gray-800 transition-colors">
                      +251 970 317 022
                    </a>
                  </p>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md overflow-hidden border-b-4 border-[#A65C4A]">
                <div className="p-6 text-center">
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Follow Us</h3>
                  <div className="flex justify-center gap-6">
                    <a
                      href="https://web.facebook.com/profile.php?id=61574941322459"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 rounded-full text-[#A65C4A] hover:bg-gray-200 transition-colors"
                    >
                      <Facebook className="h-6 w-6" />
                    </a>
                    <a
                      href="https://x.com/AfricaMaritimeC"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 rounded-full text-[#A65C4A] hover:bg-gray-200 transition-colors"
                    >
                      <Twitter className="h-6 w-6" />
                    </a>
                    <a
                      href="https://www.linkedin.com/company/africa-maritime-conference/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 rounded-full text-[#A65C4A] hover:bg-gray-200 transition-colors"
                    >
                      <Linkedin className="h-6 w-6" />
                    </a>
                    <a
                      href="https://www.instagram.com/africamaritimeconference/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-3 bg-gray-100 rounded-full text-[#A65C4A] hover:bg-gray-200 transition-colors"
                    >
                      <Instagram className="h-6 w-6" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Inline Ad between sections */}
      <div className="max-w-6xl mx-auto px-4">
        <InlineAd sponsor={sponsors.home[0]} />
      </div>

      <div className="bg-primary-navy/5 py-16 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-lg shadow-md overflow-hidden border-b-4 border-[#A65C4A]">
            <div className="w-full h-[450px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3940.6768371091!2d38.78781907489547!3d8.987674906004694!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x164b853c14f0e2d5%3A0x32ce0404870c8088!2sEthiopian%20Skylight%20Hotel!5e0!3m2!1sen!2sus!4v1717573392062!5m2!1sen!2sus"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Map of Ethiopian Skylight Hotel"
              ></iframe>
            </div>
            <div className="p-6 bg-gray-100 text-gray-800">
              <div className="flex items-center gap-3 mb-2">
                <MapPin className="h-5 w-5 text-[#A65C4A]" />
                <h3 className="text-xl font-bold text-gray-800">Ethiopian Skylight Hotel</h3>
              </div>
              <p className="ml-8 text-gray-600">Bole Sub-City, Addis Ababa, Ethiopia</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom subtle ad strip */}
      <div className="bg-gray-50 border-t border-gray-200 py-3">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-center gap-6 text-center">
            <span className="text-xs text-gray-500">Conference Partners:</span>
            <div className="flex items-center gap-4">
              {sponsors.about.slice(0, 3).map((sponsor, index) => (
                <a
                  key={index}
                  href={sponsor.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                >
                  <img
                    src={sponsor.logo || "/placeholder.svg"}
                    alt={sponsor.name}
                    className="h-5 w-auto max-w-[60px] object-contain"
                  />
                  <span className="text-xs text-gray-600 hidden sm:inline">{sponsor.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ContactUs
